import { useEffect, useState } from 'react'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import { shallowEqual } from 'hooks/reduxHooks'
import { arrayToObject } from 'lib/array/arrayUtils'
import { objectKeys } from 'lib/object/objectUtils'
import type { TripDetailsCheck } from 'tripPlanner/api/tripDetails'
import { useTripDetailsCheckQuery } from 'tripPlanner/hooks/api/tripDetails'

// Long enough that a question only follows a real pause, not the gap between words.
export const TRIP_DETAILS_DEBOUNCE_MS = 800
// Fewer words can't mention a detail clearly enough to be worth a request.
const MIN_WORDS = 3
// Two failures in a row means the check is down, not a blip; stop asking until it recovers.
const MAX_CONSECUTIVE_FAILURES = 2
// svc-trip rejects longer drafts; a trip's details sit at the start of a message.
const MAX_CHECKED_LENGTH = 4_000

export const NO_TRIP_DETAILS: TripDetailsCheck = {
  travellers: false,
  destination: false,
  dates: false,
  budget: false,
  preferences: false,
}

interface CheckedDraft {
  text: string
  mentioned: TripDetailsCheck
}

const EMPTY_CHECK: CheckedDraft = { text: '', mentioned: NO_TRIP_DETAILS }

interface Failures {
  count: number
  lastFailedAt: number
}

const NO_FAILURES: Failures = { count: 0, lastFailedAt: 0 }

/** Keeps every detail either check mentioned; returns `previous` when nothing new is mentioned. */
function mergeTripDetails(previous: TripDetailsCheck, next: TripDetailsCheck): TripDetailsCheck {
  const merged = arrayToObject(objectKeys(previous), detail => detail, detail => previous[detail] || next[detail])
  return shallowEqual(merged, previous) ? previous : merged
}

interface Options {
  /** The text arrived whole, such as an uploaded transcript or a tapped answer, so it's checked without waiting for a pause */
  isWhole: boolean
}

export interface TripDetailsCheckResult {
  mentioned: TripDetailsCheck
  /** The last finished check was of this exact draft */
  isCurrent: boolean
  /** The last check of this exact draft failed */
  hasFailed: boolean
  /** The draft has enough words to be checked */
  isLongEnough: boolean
  /** False once checks keep failing, until one succeeds */
  isAvailable: boolean
}

/**
 * Which trip details a landing draft mentions, checked as the customer types or speaks.
 * While the draft only grows, mentioned details accumulate, so model wobble near a threshold can't drop
 * something already said. Any other edit shows its own check, so a deleted or rewritten detail drops out.
 */
export function useTripDetailsCheck(text: string, { isWhole }: Options): TripDetailsCheckResult {
  const trimmed = text.trim()
  const checkedText = trimmed.slice(0, MAX_CHECKED_LENGTH)
  const isLongEnough = trimmed.split(/\s+/).length >= MIN_WORDS
  const [debouncedText, setDebouncedText] = useState('')
  const updateDebouncedText = useDebouncedCallback(setDebouncedText, TRIP_DETAILS_DEBOUNCE_MS, [])
  const { data, isPreviousData, isFetching, isError, errorUpdatedAt } = useTripDetailsCheckQuery(debouncedText)

  useEffect(() => {
    if (!isLongEnough) {
      updateDebouncedText.cancel()
      setDebouncedText('')
      return
    }
    // Moving to newer text mid-check would drop that check's answer; the newest text goes next.
    if (isFetching) {
      updateDebouncedText.cancel()
      return
    }

    updateDebouncedText(checkedText)
    if (isWhole) {
      updateDebouncedText.flush()
    }
  }, [checkedText, isFetching, isLongEnough, isWhole, updateDebouncedText])

  const [checked, setChecked] = useState(EMPTY_CHECK)
  const [failures, setFailures] = useState(NO_FAILURES)

  useEffect(() => {
    if (!trimmed) {
      setChecked(EMPTY_CHECK)
    }
  }, [trimmed])

  useEffect(() => {
    // keepPreviousData would otherwise merge an old answer back in just after the draft is emptied.
    if (data && !isPreviousData && isLongEnough) {
      setFailures(previous => (previous.count ? { ...previous, count: 0 } : previous))
      setChecked(previous => ({
        text: debouncedText,
        mentioned: debouncedText.startsWith(previous.text) ? mergeTripDetails(previous.mentioned, data) : data,
      }))
    }
  }, [data, debouncedText, isLongEnough, isPreviousData])

  useEffect(() => {
    // Going back to a draft whose check failed shows that old failure again, which isn't a new one.
    setFailures(previous => (errorUpdatedAt > previous.lastFailedAt ? { count: previous.count + 1, lastFailedAt: errorUpdatedAt } : previous))
  }, [errorUpdatedAt])

  return {
    mentioned: checked.mentioned,
    // A draft over the length limit is never wholly checked.
    isCurrent: isLongEnough && checked.text === trimmed,
    hasFailed: isError && debouncedText === checkedText,
    isLongEnough,
    isAvailable: failures.count < MAX_CONSECUTIVE_FAILURES,
  }
}
